import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  enabled?: boolean;
  disabled?: boolean;
  pullDownThreshold?: number;
  maxPullDown?: number;
  refreshingContent?: ReactNode;
  pullingContent?: ReactNode;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  enabled = false,
  disabled = false,
  pullDownThreshold = 80,
  maxPullDown = 150,
  refreshingContent,
  pullingContent,
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only enable on mobile platforms
  const isMobile = Capacitor.isNativePlatform() || /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    if (!enabled || disabled || !isMobile) return;

    const container = containerRef.current;
    if (!container) return;


    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if at the top of the scroll
      if (container.scrollTop === 0 && !isRefreshing) {
        setStartY(e.touches[0].clientY);
        setIsPulling(false);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startY === 0 || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      // Only engage pull-to-refresh if:
      // 1. Pulling down (distance > 0)
      // 2. At the very top of scroll (scrollTop === 0)
      // 3. Pulled at least 10px to distinguish from tap
      if (distance > 10 && container.scrollTop === 0) {
        setIsPulling(true);
        // Prevent default scrolling when pulling down
        e.preventDefault();
        
        // Apply resistance curve
        const resistance = Math.min(distance * 0.5, maxPullDown);
        setPullDistance(resistance);
      } else if (distance < -5) {
        // User is scrolling up, cancel pull
        setIsPulling(false);
        setPullDistance(0);
        setStartY(0);
      }
    };

    const handleTouchEnd = async () => {
      if (isPulling && pullDistance >= pullDownThreshold && !isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(pullDownThreshold);
        
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh error:', error);
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setStartY(0);
          setIsPulling(false);
        }
      } else {
        // Animate back to 0
        setPullDistance(0);
        setStartY(0);
        setIsPulling(false);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [startY, pullDistance, isRefreshing, isPulling, enabled, disabled, pullDownThreshold, maxPullDown, onRefresh, isMobile]);

  if (!enabled || !isMobile) {
    // On desktop or when disabled, just render children without pull-to-refresh
    return <>{children}</>;
  }

  const renderPullIndicator = () => {
    if (isRefreshing) {
      return refreshingContent || (
        <div className="flex items-center justify-center gap-2 text-blue-600">
          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm font-medium">Refreshing...</span>
        </div>
      );
    }

    if (pullDistance > 0) {
      const progress = Math.min((pullDistance / pullDownThreshold) * 100, 100);
      return pullingContent || (
        <div className="flex flex-col items-center justify-center gap-1">
          <svg 
            className={`h-5 w-5 text-blue-600 transition-transform ${pullDistance >= pullDownThreshold ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            style={{ transform: `rotate(${(progress / 100) * 180}deg)` }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          <span className="text-xs text-gray-500">
            {pullDistance >= pullDownThreshold ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div 
      ref={containerRef}
      className="relative overflow-auto h-full grow"
      style={{
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Pull indicator */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-200 ease-out z-50"
        style={{
          height: `${pullDistance}px`,
          transform: `translateY(-${Math.max(0, pullDownThreshold - pullDistance)}px)`,
          opacity: pullDistance > 0 ? 1 : 0,
        }}
      >
        {renderPullIndicator()}
      </div>

      {/* Content container */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: `translateY(${pullDistance}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
